import posthog from 'posthog-js';
import React from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import Link from 'next/link';
import { H3, S1Styles, S2 } from '../Typography';
import { parseCreatorCollectionMetafields, renderDefaultCreatorLogoText } from '../../utils/collections';
import { ResultType } from '../Header';
import { isUserLoggedIn } from '../../utils/analytics';
import { useStore } from '../../store';

const CreatorsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CreatorsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    grid-template-columns: repeat(auto-fill, minmax(calc(30%), 1fr));
  }
`;

const CreatorSearchCard = styled.div`
  padding: 10px;
  border-radius: 0 12px 12px 12px;
  border: 1px solid var(--opacity-15, rgba(20, 20, 20, 0.15));
  display: flex;
  width: 100%;
`;

const CreatorSearchCardContent = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SearchResultImageWrapper = styled.div`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    width: 90px;
    height: 90px;
  }
`;

const CreatorName = styled(S2)`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    ${S1Styles}
  }
`;

const DefaultCreatorLogo = styled.div`
  display: flex;
  border: ${({ theme }) => `1px solid ${theme.colors.defaultCreatorLogoBorder}`};
  background: ${({ theme }) => theme.colors.defaultCreatorLogoBackground};
  width: 72px;
  height: 72px;
`;

const DefaultCreatorLogoText = styled(H3)`
  font-size: 40px;
  margin: auto;
`;

const Hyperlink = styled.a``;

type CreatorsDisplayProps = {
  creators: Shopify.CollectionEdge[];
  titleForTracking?: string;
  query?: string;
};

const CreatorsDisplay: React.FC<CreatorsDisplayProps> = ({
  creators,
  titleForTracking,
  query,
}: CreatorsDisplayProps) => {
  const { state } = useStore();

  return (
    <CreatorsContainer>
      <CreatorsWrapper>
        {creators.map(({ node }) => {
          const { creatorLogoURL, creatorAvatarURL } = parseCreatorCollectionMetafields(node.metafields);
          const { title, handle } = node;
          return (
            <div
              key={handle}
              onClick={() => {
                titleForTracking === 'search' &&
                  posthog.capture('search_result_click', {
                    search_query: query?.trim(),
                    location: 'search_results_page',
                    is_logged_in: isUserLoggedIn(state.user),
                    result_type: ResultType.Creator,
                  });
              }}
            >
              <Link href={`/shop/creator/${handle}`} passHref>
                <Hyperlink aria-label={`Go to ${title} shop page`}>
                  <CreatorSearchCard>
                    <CreatorSearchCardContent>
                      <>
                        <SearchResultImageWrapper>
                          {creatorLogoURL || creatorAvatarURL ? (
                            <Image
                              src={creatorLogoURL || creatorAvatarURL || ''}
                              alt={`${node.title} creator logo`}
                              width={90}
                              height={90}
                            />
                          ) : (
                            <DefaultCreatorLogo>
                              <DefaultCreatorLogoText>{renderDefaultCreatorLogoText(title)}</DefaultCreatorLogoText>
                            </DefaultCreatorLogo>
                          )}
                        </SearchResultImageWrapper>
                        <CreatorName>{title}</CreatorName>
                      </>
                    </CreatorSearchCardContent>
                  </CreatorSearchCard>
                </Hyperlink>
              </Link>
            </div>
          );
        })}
      </CreatorsWrapper>
    </CreatorsContainer>
  );
};

export default CreatorsDisplay;
